import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

type SeoPageKey = 'home' | 'products' | 'about';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly translate = inject(TranslateService);
  private readonly document = inject(DOCUMENT);

  // Set to your production domain (used for canonical/og:url).
  private readonly siteOrigin = 'https://westpackaging.cl';

  init(): void {
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(() => {
      this.apply();
    });

    this.translate.onLangChange.subscribe(() => {
      this.apply();
    });

    // Initial paint
    this.apply();
  }

  private apply(): void {
    const page = this.pageKeyFromUrl(this.router.url);
    const url = this.canonicalUrlFor(page);

    const titleKey = `seo.pages.${page}.title`;
    const descKey = `seo.pages.${page}.description`;
    const keywordsKey = `seo.pages.${page}.keywords`;
    const siteNameKey = `seo.siteName`;

    this.translate.get([titleKey, descKey, keywordsKey, siteNameKey]).subscribe((t) => {
      const pageTitle = t[titleKey] || 'Western Packaging';
      const pageDesc = t[descKey] || '';
      const pageKeywords = t[keywordsKey] || '';
      const siteName = t[siteNameKey] || 'Western Packaging';

      this.title.setTitle(pageTitle);

      this.meta.updateTag({ name: 'description', content: pageDesc }, "name='description'");
      this.meta.updateTag({ name: 'keywords', content: pageKeywords }, "name='keywords'");
      this.meta.updateTag({ property: 'og:title', content: pageTitle }, "property='og:title'");
      this.meta.updateTag({ property: 'og:description', content: pageDesc }, "property='og:description'");
      this.meta.updateTag({ property: 'og:url', content: url }, "property='og:url'");
      this.meta.updateTag({ property: 'og:site_name', content: siteName }, "property='og:site_name'");

      this.meta.updateTag({ name: 'twitter:title', content: pageTitle }, "name='twitter:title'");
      this.meta.updateTag({ name: 'twitter:description', content: pageDesc }, "name='twitter:description'");

      this.setCanonical(url);
      this.setJsonLd(siteName);
    });
  }

  private pageKeyFromUrl(url: string): SeoPageKey {
    const path = url.split('?')[0]?.split('#')[0] ?? '/';
    if (path === '/products') return 'products';
    if (path === '/about') return 'about';
    return 'home';
  }

  private canonicalUrlFor(page: SeoPageKey): string {
    if (page === 'products') return `${this.siteOrigin}/products`;
    if (page === 'about') return `${this.siteOrigin}/about`;
    return `${this.siteOrigin}/`;
  }

  private setCanonical(url: string): void {
    const head = this.document.head;
    let link = head.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private setJsonLd(siteName: string): void {
    const head = this.document.head;
    const id = 'wp-jsonld';
    let script = head.querySelector<HTMLScriptElement>(`script#${id}`);
    if (!script) {
      script = this.document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      head.appendChild(script);
    }

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: siteName,
      url: this.siteOrigin,
      logo: `${this.siteOrigin}/assets/images/w-01.png`,
      sameAs: ['https://www.linkedin.com/company/western-packaging/'],
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Vitacura',
        addressRegion: 'Santiago Metropolitan Region',
        addressCountry: 'CL'
      },
      areaServed: ['CL'],
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '+56988275330',
          contactType: 'customer support',
          availableLanguage: ['en', 'es', 'pt']
        }
      ]
    };

    script.text = JSON.stringify(jsonLd);
  }
}

